import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/travis/build/sgrishchenko/reselect-utils/node_modules/gatsby-theme-docz/src/base/Layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "quick-start"
    }}>{`Quick Start`}</h1>
    <h2 {...{
      "id": "path-selector"
    }}>{`Path Selector`}</h2>
    <p>{`Suppose you have such a normalized state:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const state = {
  persons: {
    1: {
      id: 1,
      firstName: 'Marry',
      secondName: 'Poppins',
    },
    2: {
      id: 2,
      firstName: 'Harry',
      secondName: 'Potter',
    },
  },
};
`}</code></pre>
    <p>{`And you want to build something like this selector:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const personFullNameSelector = createSelector(
  (state, props) => state.persons[props.personId].firstName,
  (state, props) => state.persons[props.personId].secondName,
  (firstName, secondName) => \`\${firstName} \${secondName}\`,
);
`}</code></pre>
    <p>{`As you can see in the dependencies of this selector quite a lot of boilerplate code. The problem of duplicate code can be solved with `}<inlineCode parentName="p">{`createPathSelector`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { createPathSelector } from 'reselect-utils';

const personSelector = (state, props) => state.persons[props.personId];

const personFullNameSelector = createSelector(
  createPathSelector(personSelector).firstName(),
  createPathSelector(personSelector).secondName(),
  (firstName, secondName) => \`\${firstName} \${secondName}\`,
);
`}</code></pre>
    <p>{`You can also add default values if you want the selector to produce an adequate result even in the absence of the necessary `}<inlineCode parentName="p">{`person`}</inlineCode>{` in the `}<inlineCode parentName="p">{`state`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { createPathSelector } from 'reselect-utils';

const personFullNameSelector = createSelector(
  createPathSelector(personSelector).firstName('John'),
  createPathSelector(personSelector).secondName('Doe'),
  (firstName, secondName) => \`\${firstName} \${secondName}\`,
);
`}</code></pre>
    <p>{`There are short aliases for many helpers in this library, so you can re-write your code like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { path } from 'reselect-utils';

const personFullNameSelector = createSelector(
  path(personSelector).firstName('John'),
  path(personSelector).secondName('Doe'),
  (firstName, secondName) => \`\${firstName} \${secondName}\`,
);
`}</code></pre>
    <p>{`You can also work with objects of unlimited nesting:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { path } from 'reselect-utils';

const personSelectorInfo = createSelector(
  path(personSelector).address.street('-'),
  path(personSelector).some.very.deep.field(123),
  (street, field) => ({ street, field }),
);
`}</code></pre>
    <p>{`A more detailed description can be found `}<a parentName="p" {...{
        "href": "/reselect-utils/docz/guides/path-and-prop-selectors"
      }}>{`here`}</a>{`.`}</p>
    <h2 {...{
      "id": "chain-selector"
    }}>{`Chain Selector`}</h2>
    <p>{`Suppose you have such a normalized state:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const state = {
  persons: {
    1: {
      id: 1,
      firstName: 'Marry',
      secondName: 'Poppins',
    },
    2: {
      id: 2,
      firstName: 'Harry',
      secondName: 'Potter',
    },
  },

  messages: {
    100: {
      id: 100,
      personId: 1,
      text: 'Hello',
    },
    200: {
      id: 200,
      personId: 2,
      text: 'Buy',
    },
  },
};
`}</code></pre>
    <p>{`And you want to select a person by the message id. You can write something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const personSelectorByMessageId = (state, props) => {
  const message = state.messages[props.messageId];

  return state.persons[message.personId];
};
`}</code></pre>
    <p>{`This is an acceptable solution, but what if the chain is longer? `}<inlineCode parentName="p">{`Chain Selector`}</inlineCode>{` will help to solve such problems:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { chain, bound } from 'reselect-utils';

const messageSelector = (state, props) => state.messages[props.messageId];
const personSelector = (state, props) => state.persons[props.personId];

const personByMessageIdSelector = chain(messageSelector)
  .chain((message) => bound(personSelector, { personId: message.personId }))
  .build();
`}</code></pre>
    <p><inlineCode parentName="p">{`Chain Selector`}</inlineCode>{` allows you to create dynamic selectors that depend on the current state. Moreover, the callback that is passed to the `}<inlineCode parentName="p">{`chain`}</inlineCode>{` method is cached by input parameters. `}<inlineCode parentName="p">{`bound`}</inlineCode>{` binds the selector to specific parameter values and turns a parametric selector into an non parametric. And at the end you must call the `}<inlineCode parentName="p">{`build`}</inlineCode>{` method to get the normal selector. It’s like a chain of `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/Promise"
      }}>{`Promises`}</a>{`. `}<a parentName="p" {...{
        "href": "/reselect-utils/docz/guides/chain-and-empty-selectors"
      }}>{`Chain Selector`}</a>{` and `}<a parentName="p" {...{
        "href": "/reselect-utils/docz/guides/bound-and-adapted-selectors"
      }}>{`Bound Selector`}</a>{` are described in detail in Guides section.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      